import './api-keys-paged-list-page.scss';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import FontBody1 from '../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR } from '../../../components/fonts/font-props';
import IconPlus from '../../../components/icons/icon-plus/icon-plus';
import { BUTTON_VARIANTS } from '../../../components/inputs/button-variants';
import { Button } from '../../../components/inputs/button/button';
import BasePageLayout from '../../../components/layouts/base-page-layout/base-page-layout';
import Card from '../../../components/layouts/card/card';
import TableRow from '../../../components/table/components/table-row/table-row';
import Table from '../../../components/table/table';
import ActionList from './components/action-list/action-list';
import pagedListStore from './stores/api-keys-paged-list-store';
import ApiKeyEditorStore from './stores/api-key-editor-store';
import IconInfo from '../../../components/icons/icon-info/icon-info';
import { Tooltip } from '../../../components/tooltip/tooltip';
import Icon from '../../../components/icons/icon/icon';
import { ConfirmModal } from '../../../components/modal/components/confirm-modal/confirm-modal';
import { ButtonLink } from '../../../components/inputs/button-link/button-link';
import config from '../../../config';
import { Note, NOTE_TYPE } from '../../../components/note/note';
import currentPortalStore from '../../../stores/current-portal-store';
import appLayoutStore from "../../../stores/app-layout-store";
import returnGuardWarningStore from '../../../components/return-guard-warning-component/return-guard-warning-store';


interface IApiKeysPagedListPageProps {}

const ApiKeysPagedListPage: React.FC<IApiKeysPagedListPageProps> = observer(() => {
    const [keyToRemove, setKeyToRemove] = useState<ApiKeyEditorStore | null>(null);
    useEffect(() => {
        pagedListStore.loadPage(1);
        currentPortalStore.load();
    }, []);

    const onRemove = (apiKey: ApiKeyEditorStore) => {
        setKeyToRemove(apiKey);
    };

    const onRemoveApprove = async () => {
        if (keyToRemove) {
            await pagedListStore.removeApiKey(keyToRemove.integrations_connection_id);
            setKeyToRemove(null);
        }
    };

    const instructionJsx =(
            <>
                <p>
                    Use the ReturnGO API to integrate with any system or application. <br/>
                    The default quota is up to 2000 calls per day, limited to 4 calls per second.
                </p>
                <p>
                    API keys are common to all stores with the same owner. <br/>
                    This simplifies applications that work for multiple stores in the same owner account. <br/>
                    If you have separate apps for separate stores, when changing the API key, pay attention to update all apps.
                </p>
                <p>
                    NOTE: Email verification is required in order to generate API keys.
                </p>
            </>
    );

    const ownerVerificationStatus = currentPortalStore.currentPortal.ownerVerificationStatus;
    const apiReferenceBtnDefaultTop = returnGuardWarningStore.height + 25;

    return (
        <BasePageLayout title="Settings" subTitles={['Integrations', 'API']}
            instruction={instructionJsx}>
            <div className='api-keys-paged-list-page'>
                
                { !currentPortalStore.isLoading && ownerVerificationStatus && !ownerVerificationStatus.is_verified &&
                    <Note type={NOTE_TYPE.WARNING} body={
                        <span dangerouslySetInnerHTML={{ __html: ownerVerificationStatus?.message ?? '' }} />
                    } />
                }

                <Card>
                    <Table
                        isLoading={pagedListStore.isLoading}
                        pagination={{
                            onSetPage: pagedListStore.loadPage,
                            page: pagedListStore.page,
                            total: pagedListStore.total,
                            perPage: pagedListStore.perPage
                        }}
                        sort={{
                            onSort: pagedListStore.sortData,
                            by: pagedListStore.sortBy,
                            dir: pagedListStore.sortDir,
                        }}
                        totalResultsTitle='Keys'
                        columns={[
                            {
                                content: 'Date created',
                                sortBy: 'created_at',
                                sortable: true,
                                width: {
                                    value: 1,
                                    type: 'fr'
                                }
                            },
                            {
                                content: 'API key',
                                width: {
                                    value: 2,
                                    type: 'fr'
                                }
                            },
                            {
                                content:
                                    <div className='table-header'>
                                        Used for/with
                                        <Tooltip content='Use to document the purpose of the key'>
                                            <Icon Component={IconInfo} />
                                        </Tooltip>
                                    </div>
                                ,
                                width: {
                                    value: 1.5,
                                    type: 'fr'
                                }
                            },
                            {
                                content: 'Actions',
                                width: {
                                    value: 160,
                                    type: 'px'
                                }
                            },
                        ]}
                    >
                        {pagedListStore.total > 0 && pagedListStore.list.map((item: ApiKeyEditorStore) => (
                            <TableRow
                                key={item.id}
                                cells={[
                                    <FontBody1 color={FONT_COLOR.PRIMARY2}>
                                        {item.created_at}
                                    </FontBody1>,
                                    <FontBody1 color={FONT_COLOR.PRIMARY2}>
                                        {item.api_key}
                                    </FontBody1>,
                                    <FontBody1 color={FONT_COLOR.PRIMARY2}>
                                        {item.name}
                                    </FontBody1>,
                                    item.id && <ActionList apiKey={item} isDisabled={pagedListStore.isLoading} onRemove={onRemove} />
                                ]}
                            />
                        ))}
                    </Table>
                </Card>
                <div>
                    <Button
                        leftIcon={IconPlus}
                        variant={BUTTON_VARIANTS.Transparent}
                        text="Add api key"
                        onClick={() => pagedListStore.addApiKey()}
                        disabled={pagedListStore.isLoading || pagedListStore.total > 0 || !ownerVerificationStatus?.is_verified }
                    />
                </div>
            </div>
            <div className='api-reference-button' style={{top: (apiReferenceBtnDefaultTop + appLayoutStore.headerHeight)}}>
                <ButtonLink
                    variant={BUTTON_VARIANTS.Secondary}
                    href={config.apiRefUrl}
                    text='API Reference'
                />
            </div>
            {keyToRemove &&
                <ConfirmModal
                    title='Delete API key'
                    text={keyToRemove.name ?
                        <>
                            You asked to delete the API key used for/with <strong>{keyToRemove.name}</strong>. 
                            Once deleted you won't be able to restore this key.<br></br>
                            Are you sure you want to delete this key?
                        </>
                    :
                        <>
                            `You asked to delete the API key <strong>{keyToRemove.api_key}</strong>. 
                            Once deleted you won't be able to restore this key.<br></br>
                            Are you sure you want to delete this key?`
                        </>
                    }
                    cancelButton={{ 
                        title: 'Cancel', 
                        onClick: () => setKeyToRemove(null) 
                    }} 
                    okButton={{ 
                        title: 'Delete', 
                        onClick: onRemoveApprove,
                    }}
                />
            }
        </BasePageLayout>
    )
});

export default ApiKeysPagedListPage;
